/**
 * CommunityPillBar — the single Chat / Feed / Events / Recordings / Rewards /
 * Directory bar.
 *
 * It was hand-copied into pages/Community.tsx, pages/Events.tsx and
 * pages/EventDetail.tsx, with a fourth silent copy of its geometry living in
 * PillBarShell's hardcoded pill widths (whose header comment already flagged
 * the drift risk). Adding the Recordings pill would have meant editing four
 * places, so the bar lives here now and PillBarShell derives its skeleton
 * widths from the SAME `COMMUNITY_PILLS` array — the drift is structurally
 * impossible rather than merely discouraged.
 *
 * Two kinds of pill:
 *   - `tab`   — a view INSIDE pages/Community.tsx, which owns it as local
 *               state. The bar can't know how a given call site switches
 *               views, so it delegates to `onSelectTab`: Community sets its
 *               own state, every other page navigates to /community?tab=<id>.
 *   - `route` — its own route. The bar navigates itself; nothing to wire up.
 *
 * Pill order deliberately keeps the shipped order (Chat · Feed · Events ·
 * Rewards · Directory) and inserts Recordings beside Events. Re-ordering the
 * five existing pills would move nav that users already know, which is a
 * separate decision from adding a sixth.
 */

import { isSpeediesEnabled } from '@/lib/feature-flags';
import { useEffect, useRef } from 'react';
import { Calendar, Flame, Globe, MessageSquare, Users, Video } from 'lucide-react';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { markNavSource } from '@/lib/tracking/nav-source';
import type { LucideIcon } from 'lucide-react';

/** Views that live inside pages/Community.tsx rather than on their own route. */
export type CommunityTabId = 'chats' | 'feed' | 'rewards' | 'directory';

/** Every pill id, including the ones that are their own route. */
export type CommunityPillId = CommunityTabId | 'events' | 'recordings';

type CommunityPill =
  & {
    id: CommunityPillId;
    label: string;
    icon: LucideIcon;
    /**
     * Width the skeleton stands in with. Roughly the rendered pill: px-4 (32)
     * + icon (14) + gap-1.5 (6) + the label at 13px semibold. Lives beside the
     * label so a renamed pill can't leave a stale width behind.
     */
    skeletonWidth: string;
  }
  & (
    | { kind: 'tab' }
    | { kind: 'route'; to: '/events' | '/recordings' }
  );

export const COMMUNITY_PILLS: readonly CommunityPill[] = [
  { id: 'chats', label: 'Chat', icon: MessageSquare, kind: 'tab', skeletonWidth: 'w-16' },
  { id: 'feed', label: 'Feed', icon: Globe, kind: 'tab', skeletonWidth: 'w-16' },
  { id: 'events', label: 'Events', icon: Calendar, kind: 'route', to: '/events', skeletonWidth: 'w-[74px]' },
  { id: 'recordings', label: 'Recordings', icon: Video, kind: 'route', to: '/recordings', skeletonWidth: 'w-[104px]' },
  { id: 'rewards', label: 'Rewards', icon: Flame, kind: 'tab', skeletonWidth: 'w-[86px]' },
  { id: 'directory', label: 'Directory', icon: Users, kind: 'tab', skeletonWidth: 'w-[86px]' },
] as const;

/**
 * The pills to show now. With Speedies on, Chat is its own menu item at /chat, so its pill is
 * gone; with the flag off this is `COMMUNITY_PILLS`, unchanged. PillBarShell reads it too, so the
 * skeleton always matches the bar.
 */
export function visibleCommunityPills(speediesOn = isSpeediesEnabled()): readonly CommunityPill[] {
  return speediesOn ? COMMUNITY_PILLS.filter((p) => p.id !== 'chats') : COMMUNITY_PILLS;
}

export interface CommunityPillBarProps {
  /** Which pill reads as current. */
  activeId: CommunityPillId;
  /**
   * A `tab` pill was chosen. Community.tsx sets its local tab state; every
   * other page navigates to /community with `search: { tab }`.
   */
  onSelectTab: (tab: CommunityTabId) => void;
  /** Unread counts, currently Chat and Feed on the Community page only. */
  badges?: Partial<Record<CommunityPillId, number>>;
}

export function CommunityPillBar({ activeId, onSelectTab, badges }: CommunityPillBarProps) {
  const navigate = useNavigate();
  // `activeId` is appearance only. Whether a route pill still navigates is a
  // separate question, answered by where we actually are: on /events the
  // Events pill is inert (it was a plain label before this component existed),
  // but on /events/$eventId it is the way back to the list — and the same
  // split applies to /recordings and /recordings/$source/$id.
  const { pathname } = useLocation();

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);

  // Six pills measure ~624px, so on a 375px phone the track scrolls and starts
  // at scrollLeft 0 — which left the active pill clipped off the right edge
  // (Recordings by 54px). Centre it instead, so the current section is always
  // the one you can see. Setting scrollLeft on the scroller (rather than
  // scrollIntoView) keeps this strictly local: it can never move the page.
  useEffect(() => {
    const scroller = scrollerRef.current;
    const active = activeRef.current;
    if (!scroller || !active) return;
    if (scroller.scrollWidth <= scroller.clientWidth) return; // nothing to scroll
    // Rect-relative, not offsetLeft: the track isn't positioned, so the
    // button's offsetParent is some ancestor of the scroller, not the scroller.
    const delta = active.getBoundingClientRect().left - scroller.getBoundingClientRect().left;
    const target = scroller.scrollLeft + delta - (scroller.clientWidth - active.offsetWidth) / 2;
    scroller.scrollLeft = Math.max(0, Math.min(target, scroller.scrollWidth - scroller.clientWidth));
  }, [activeId]);

  return (
    <div className="px-4 sm:px-6 py-3 flex items-center border-b border-border bg-card/80 backdrop-blur-sm z-40 flex-shrink-0">
      {/* min-w-0 is mandatory: without it the track can't contract and six
          pills push the whole page sideways on a phone (mobile skill,
          "Horizontal overflow"). `.nav-pill` carries whitespace-nowrap +
          flex-shrink-0, so the row scrolls instead of squashing. */}
      <div ref={scrollerRef} className="flex-1 min-w-0 flex overflow-x-auto scrollbar-hide">
        <nav className="nav-pill-container mx-auto flex-shrink-0" aria-label="Community sections">
          {visibleCommunityPills().map((pill) => {
            const Icon = pill.icon;
            const isActive = activeId === pill.id;
            const badge = badges?.[pill.id] ?? 0;
            return (
              <button
                key={pill.id}
                ref={isActive ? activeRef : undefined}
                type="button"
                onClick={() => {
                  markNavSource('pill');
                  if (pill.kind === 'route') {
                    if (pathname.replace(/\/+$/, '') === pill.to) return;
                    // Always land on the target route in its default state;
                    // don't carry the current page's filters across.
                    navigate({ to: pill.to, search: {} });
                    return;
                  }
                  onSelectTab(pill.id as CommunityTabId);
                }}
                className={`nav-pill flex items-center gap-1.5 ${
                  isActive ? 'nav-pill--active' : 'nav-pill--inactive hover:text-foreground'
                }`}
                aria-current={isActive ? 'page' : undefined}
              >
                <Icon className="w-3.5 h-3.5" />
                {pill.label}
                {badge > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full flex items-center justify-center">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </button>
            );
          })}
        </nav>
      </div>
    </div>
  );
}
